<template>
    <VJoyPanel>
        <template #panel-title>
            {{ $t('projectclient.projectview.details.documents.title') }}
        </template>
        <template #panel-body>
            <div class="project-details-freelancer-document">
                <div class="project-details-freelancer-document_downloads / flex">
                    <VJoyButton
                        v-if="projectInformation.invoiceOrderSummaryTypes.some((s) => ['MISSION_ORDER', 'MISSION_SUMMARY'].includes(s))"
                        icon="download"
                        variant="secondary"
                        size="small"
                        @click="downloadDocument('ORDER_SUMMARY')"
                    >
                        {{ $t('projectclient.projectview.details.documents.download-summary.button') }}
                    </VJoyButton>
                    <VJoyButton
                        v-if="projectInformation.invoiceOrderSummaryTypes.includes('PROFORMA')"
                        icon="download"
                        variant="secondary"
                        size="small"
                        @click="downloadDocument('PROFORMA')"
                    >
                        {{ $t('projectclient.projectview.details.documents.download-proforma.button') }}
                    </VJoyButton>
                    <VJoyButton
                        v-if="projectInformation.freelancerLegalDocumentCanBeDownloaded"
                        icon="download"
                        variant="secondary"
                        size="small"
                        @click="downloadDocument('FREELANCER-LEGAL-DOCUMENTS')"
                    >
                        {{ $t('projectclient.projectview.details.documents.download-freelancer-legal-documents.button') }}
                    </VJoyButton>
                    <VJoyButton
                        v-if="projectInformation.invoiceOrderSummaryTypes.includes('MISSION_BUNDLE')"
                        icon="download"
                        variant="secondary"
                        size="small"
                        @click="downloadDocument('MISSION_BUNDLE')"
                    >
                        {{ $t('projectclient.projectview.details.documents.download-contract-summary.button') }}
                    </VJoyButton>
                </div>
                <template
                    v-if="
                        projectInformation.invoiceOrderSummaryTypes.some((s) =>
                            ['MISSION_SUMMARY_WITH_CONDITIONS', 'MISSION_WITH_CONDITIONS'].includes(s),
                        )
                    "
                >
                    <h4>{{ $t('projectclient.projectview.details.documents.contract.subtitle') }}</h4>
                    <br />
                    <div>
                        <VJoyButton icon="download" variant="secondary" size="small" @click="downloadDocument('FULL-CONTRACT')">
                            {{ $t('projectclient.projectview.details.documents.download-contract.button') }}
                        </VJoyButton>
                    </div>
                </template>

                <h4>{{ $t('projectclient.projectview.details.documents.appendix.subtitle') }}</h4>

                <div>
                    <VJoyWrapper v-if="projectInformation.contract.appendix.fileName">
                        <ViewDocument
                            :name="projectInformation.contract.appendix.fileName"
                            :download-url="projectInformation.contract.appendix.fileUrl"
                            :upload-date="projectInformation.contract.appendix.uploadedDate"
                            :hide-preview="true"
                        />
                    </VJoyWrapper>

                    <VJoyWrapper v-if="showUploadAppendixFile && projectInformation.contract.appendix.fileName">
                        <VJoyHighlight level="warning">
                            {{
                                $t('projectclient.projectview.details.documents.appendix.upload.warning', {
                                    0: projectInformation.contract.appendix.fileName,
                                })
                            }}
                        </VJoyHighlight>
                    </VJoyWrapper>
                    <VJoyButton v-if="!showUploadAppendixFile" icon="add" variant="ghost" size="small" @click="showUploadAppendixFile = true">
                        {{ $t('projectclient.projectview.details.documents.appendix.add-appendix.button') }}
                    </VJoyButton>
                    <template v-else>
                        <VJoyWrapper :item-stretch="true">
                            <VJoyDropzone
                                v-model="appendixFile"
                                data-testid="project-view-details-upload-dropzone"
                                name="appendixUpload"
                                :accept="['application/pdf']"
                            >
                                <template #dropzone-description>
                                    {{ $t('projectclient.projectview.details.documents.appendix.upload.description') }}
                                    <strong>{{ $t('projectclient.projectview.details.documents.appendix.upload.description.strong-text') }}</strong>
                                </template>
                            </VJoyDropzone>
                        </VJoyWrapper>
                        <VJoyButton
                            :disabled="!appendixFile.length"
                            variant="main"
                            size="small"
                            :loading="loadingAppendixFile"
                            @click="uploadAppendixFile"
                        >
                            {{ $t('projectclient.projectview.details.documents.appendix.upload.confirm') }}
                        </VJoyButton>
                    </template>
                </div>
            </div>
        </template>
    </VJoyPanel>
</template>

<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useFormat, useTranslation} from '#imports';
    import {VJoyButton, VJoyDropzone, VJoyHighlight, VJoyPanel, VJoyWrapper} from '@maltjoy/core-vue';
    import {ViewDocument} from '~/common/ui';
    import {ProjectInformationResourceInvoiceOrderSummaryTypesEnum} from '~/openapi';
    import {useProjectInformationStore} from '@/common/stores';

    const {t} = useTranslation();

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const {formatDate} = useFormat();

    const showUploadAppendixFile = ref(false);
    const loadingAppendixFile = ref(false);
    const appendixFile = ref<File[]>([]);

    function downloadDocument(
        type:
            | Exclude<ProjectInformationResourceInvoiceOrderSummaryTypesEnum, 'MISSION_SUMMARY' | 'MISSION_ORDER' | 'INVOICE'>
            | 'ORDER_SUMMARY'
            | 'FREELANCER-LEGAL-DOCUMENTS'
            | 'FULL-CONTRACT',
    ) {
        const link = document.createElement('a');
        let title: string | undefined;

        if (type === 'ORDER_SUMMARY') {
            if (projectInformation.value.invoiceOrderSummaryTypes.includes('MISSION_ORDER')) {
                title = 'Order summary';
                link.href = `/mission/pdf/${projectInformation.value.projectId}?withConditions=true`;
            } else {
                title = 'Project summary';
                link.href = `/mission/pdf/${projectInformation.value.projectId}/summary?withConditions=true`;
            }
        } else if (type === 'MISSION_BUNDLE') {
            title = 'Full contract';
            link.href = `/project-invoices/bundle/pdf/${projectInformation.value.projectId}?withAppendices=true`;
        } else if (type === 'FREELANCER-LEGAL-DOCUMENTS') {
            title = 'Freelancer legal documents';
            link.href = `/api/mission-files/${projectInformation.value.projectId}`;
        } else if (type === 'PROFORMA') {
            title = 'Proforma';
            link.href = `/mission/pdf/${projectInformation.value.projectId}/proforma-invoice`;
        } else if (type === 'FULL-CONTRACT') {
            title = 'Full contract';
            if (
                projectInformation.value.invoiceOrderSummaryTypes.includes(
                    ProjectInformationResourceInvoiceOrderSummaryTypesEnum.MissionSummaryWithConditions,
                )
            ) {
                link.href = `/proposal/pdf/${projectInformation.value.contract.quoteId}/summary?withConditions=true`;
            } else if (
                projectInformation.value.invoiceOrderSummaryTypes.includes(
                    ProjectInformationResourceInvoiceOrderSummaryTypesEnum.MissionWithConditions,
                )
            ) {
                link.href = `/proposal/pdf/${projectInformation.value.contract.quoteId}?withConditions=true`;
            }
        }

        if (title && link.href) {
            title = `${projectInformation.value.contract.subject} - ${title}`;
            title += '.pdf';

            link.setAttribute('download', title);
            link.click();
        }
    }

    async function uploadAppendixFile() {
        try {
            if (appendixFile.value.length) {
                loadingAppendixFile.value = true;
                await projectInformationStore.uploadProjectAppendixFile(appendixFile.value[0]);
                showUploadAppendixFile.value = false;
                appendixFile.value = [];
            }
        } catch (e) {
        } finally {
            loadingAppendixFile.value = false;
        }
    }
</script>

<style lang="scss" scoped>
    .project-details-freelancer-document {
        &_downloads {
            gap: var(--joy-core-spacing-4);
        }
    }

    h4 {
        margin-top: var(--joy-core-spacing-6);
        margin-bottom: var(--joy-core-spacing-4);
    }

    br {
        display: block;
        content: '';
        margin-top: var(--joy-core-spacing-4) !important;
    }
</style>
